import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';
import { FaSpinner, FaDesktop, FaMobileAlt } from 'react-icons/fa';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const ANALYTICS_API = 'https://api.vuebillboards.com';

const thClass =
  'whitespace-nowrap border-b border-slate-200 px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500';
const tdClass = 'border-b border-slate-100 px-4 py-2.5 align-top text-[13px] text-slate-800';

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function fetchJson(path) {
  const headers = await authHeaders();
  const res = await fetch(`${ANALYTICS_API}${path}`, { headers });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.error || `Request failed (${res.status})`);
  }
  return json;
}

function formatActivityVisit(record) {
  const action = record.action || record.details?.action || 'unknown';
  const page = record.page || record.details?.page;
  const signId = record.signId || record.details?.signId;
  const queryText = record.query || record.details?.query;
  const campaignId = record.campaignId || record.details?.campaignId;

  if (action === 'sign_view') {
    const base = signId ? `Viewed sign ${signId}` : 'Sign view';
    return page ? `${base} (${page})` : base;
  }
  if (action === 'page_view') return page ? `Visited ${page}` : 'Page view';
  if (action === 'search') return queryText ? `Search: "${queryText}"` : 'Search';
  const parts = [String(action).replace(/_/g, ' ')];
  if (page) parts.push(`on ${page}`);
  if (signId) parts.push(`sign ${signId}`);
  if (queryText) parts.push(`"${queryText}"`);
  if (campaignId) parts.push(`campaign ${campaignId}`);
  return parts.join(' · ');
}

function PlatformBadge({ platform }) {
  const p = String(platform || '').toLowerCase();
  if (p === 'web') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800">
        <FaDesktop className="shrink-0 text-[10px]" aria-hidden />
        Web
      </span>
    );
  }
  if (p === 'ios' || p === 'android' || p === 'mobile') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800">
        <FaMobileAlt className="shrink-0 text-[10px]" aria-hidden />
        {p === 'android' ? 'Android' : p === 'ios' ? 'iOS' : 'App'}
      </span>
    );
  }
  return <span className="text-xs text-slate-400">{platform || '—'}</span>;
}

function pickUserLabel(data, uid) {
  if (!data) return uid || 'Unknown';
  const combined = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  return data.name || data.displayName || data.userName || data.email || data.companyName || uid || 'Unknown';
}

const CHART_METRICS = {
  dau: { label: 'DAU', color: '#a78bfa', key: 'dauByDay' },
  signups: { label: 'Signups', color: '#0b7fd4', key: 'signupsByDay' },
};

/**
 * Postgres-backed activity reviewer via vue-analytics-api → user-activity-service.
 * Firebase Firestore activity remains on the other Analytics tabs.
 */
export default function UserActivityReviewer() {
  const [days, setDays] = useState(30);
  const [activeHours, setActiveHours] = useState(24);
  const [chartMetric, setChartMetric] = useState('dau');
  const [growth, setGrowth] = useState(null);
  const [active, setActive] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

  const [uidInput, setUidInput] = useState('');
  const [actionsFilter, setActionsFilter] = useState('');
  const [eventLimit, setEventLimit] = useState(100);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [lookedUpUser, setLookedUpUser] = useState(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError('');
    try {
      const [growthJson, activeJson] = await Promise.all([
        fetchJson(`/api/admin/user-activity/stats/growth?days=${encodeURIComponent(days)}`),
        fetchJson(`/api/admin/user-activity/stats/active?hours=${encodeURIComponent(activeHours)}`),
      ]);
      setGrowth(growthJson);
      setActive(activeJson);
    } catch (e) {
      console.error(e);
      setStatsError(e.message || 'Failed to load activity stats');
      setGrowth(null);
      setActive(null);
    } finally {
      setStatsLoading(false);
    }
  }, [days, activeHours]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const chartData = useMemo(() => {
    if (!growth) return [];
    const meta = CHART_METRICS[chartMetric];
    const rows = Array.isArray(growth[meta.key]) ? growth[meta.key] : [];
    return rows.map((r) => ({
      date: r.day,
      count: Number(r.count) || 0,
    }));
  }, [growth, chartMetric]);

  const platformRows = useMemo(() => {
    const rows = Array.isArray(growth?.eventsByPlatform) ? growth.eventsByPlatform : [];
    return rows.map((r) => ({ platform: r.platform, count: Number(r.count) || 0 }));
  }, [growth]);

  const totalSignups = useMemo(
    () => (Array.isArray(growth?.signupsByDay) ? growth.signupsByDay.reduce((s, r) => s + (Number(r.count) || 0), 0) : 0),
    [growth]
  );
  const latestDau = useMemo(() => {
    const rows = Array.isArray(growth?.dauByDay) ? growth.dauByDay : [];
    if (!rows.length) return 0;
    return Number(rows[rows.length - 1].count) || 0;
  }, [growth]);

  const loadUserEvents = async (e) => {
    e?.preventDefault?.();
    const uid = String(uidInput || '').trim();
    if (!uid) {
      setEventsError('Enter a Firebase UID');
      return;
    }
    setEventsLoading(true);
    setEventsError('');
    setLookedUpUser(null);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', String(eventLimit));
      const actions = String(actionsFilter || '')
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
        .join(',');
      if (actions) qs.set('actions', actions);

      const json = await fetchJson(
        `/api/admin/user-activity/users/${encodeURIComponent(uid)}/events?${qs.toString()}`
      );
      const list = Array.isArray(json.events) ? json.events : [];
      setEvents(list);
      setEventsTotal(typeof json.total === 'number' ? json.total : list.length);

      try {
        const snap = await getDoc(doc(db, 'users', uid));
        setLookedUpUser(snap.exists() ? { id: uid, ...snap.data() } : { id: uid });
      } catch {
        setLookedUpUser({ id: uid });
      }
    } catch (err) {
      console.error(err);
      setEventsError(err.message || 'Failed to load user events');
      setEvents([]);
      setEventsTotal(0);
    } finally {
      setEventsLoading(false);
    }
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-emerald-200/80 bg-emerald-50/50 px-4 py-3 text-sm text-emerald-900">
        <div>
          <div className="font-semibold">Postgres activity (user-activity-service)</div>
          <p className="mb-0 mt-0.5 text-emerald-800/90">
            Growth, active users, and per-user timelines from the upgraded event store. Firebase Events tab stays
            available until cutover.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="f-input w-[120px]"
            aria-label="Growth window"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <select
            value={activeHours}
            onChange={(e) => setActiveHours(Number(e.target.value))}
            className="f-input w-[130px]"
            aria-label="Active window"
          >
            <option value={24}>Active 24h</option>
            <option value={72}>Active 72h</option>
            <option value={168}>Active 7d</option>
          </select>
          <button type="button" className="f-button" onClick={loadStats} disabled={statsLoading}>
            Refresh
          </button>
        </div>
      </div>

      {statsError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {statsError}
        </div>
      )}

      {statsLoading ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-xl border border-slate-200/80 bg-white/90 p-10 shadow-sm">
          <FaSpinner className="fa-spin text-3xl text-primary" aria-hidden />
          <p className="text-sm font-medium text-slate-600">Loading Postgres activity stats…</p>
        </div>
      ) : (
        <>
          <div className="f-grid">
            <div className="f-card f-stat-card">
              <span className="f-stat-label">Active ({activeHours}h)</span>
              <span className="f-stat-value">{Number(active?.totalActive || 0).toLocaleString()}</span>
            </div>
            <div className="f-card f-stat-card">
              <span className="f-stat-label">Latest DAU</span>
              <span className="f-stat-value text-primary">{latestDau.toLocaleString()}</span>
            </div>
            <div className="f-card f-stat-card">
              <span className="f-stat-label">Signups ({days}d)</span>
              <span className="f-stat-value text-emerald-600">{totalSignups.toLocaleString()}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 border-b border-slate-200">
            {Object.entries(CHART_METRICS).map(([id, meta]) => (
              <button
                key={id}
                type="button"
                onClick={() => setChartMetric(id)}
                className={[
                  '-mb-px border-b-2 px-4 py-2 text-sm transition-colors',
                  chartMetric === id
                    ? 'border-primary font-semibold text-primary'
                    : 'border-transparent font-normal text-slate-500 hover:text-slate-800',
                ].join(' ')}
              >
                {meta.label}
              </button>
            ))}
          </div>

          <div className="f-card">
            <div className="h-[280px]">
              {chartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">No chart data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill={CHART_METRICS[chartMetric].color} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="f-card overflow-hidden p-0">
              <div className="border-b border-slate-100 px-5 py-3.5 text-sm font-semibold text-slate-900">
                Events by platform ({days}d)
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Platform</th>
                      <th className={`${thClass} text-right`}>Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformRows.map((r) => (
                      <tr key={r.platform}>
                        <td className={tdClass}>
                          <PlatformBadge platform={r.platform} />
                        </td>
                        <td className={`${tdClass} text-right font-semibold`}>{r.count.toLocaleString()}</td>
                      </tr>
                    ))}
                    {platformRows.length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-4 py-8 text-center text-sm text-slate-500">
                          No platform data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="f-card overflow-hidden p-0">
              <div className="border-b border-slate-100 px-5 py-3.5 text-sm font-semibold text-slate-900">
                Who&apos;s active ({activeHours}h)
              </div>
              <div className="space-y-4 p-5">
                <BreakdownList title="Platform" rows={active?.byPlatform} keyName="platform" />
                <BreakdownList title="Browser" rows={active?.byBrowser} keyName="browser" />
                <BreakdownList title="OS" rows={active?.byOs} keyName="os" />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="f-card">
        <h3 className="mb-1 text-sm font-semibold text-slate-900">User timeline</h3>
        <p className="mb-4 text-sm text-slate-500">
          Load recent Postgres events for a Firebase UID (service-secret proxy; not the CRM notes collection).
        </p>
        <form onSubmit={loadUserEvents} className="flex flex-wrap items-end gap-3">
          <label className="min-w-[220px] flex-1">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Firebase UID
            </span>
            <input
              value={uidInput}
              onChange={(e) => setUidInput(e.target.value)}
              placeholder="e.g. abc123…"
              className="f-input w-full font-mono text-sm"
            />
          </label>
          <label className="min-w-[180px] flex-1">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Actions filter
            </span>
            <input
              value={actionsFilter}
              onChange={(e) => setActionsFilter(e.target.value)}
              placeholder="sign_view,search,page_view"
              className="f-input w-full font-mono text-sm"
            />
          </label>
          <label className="w-[110px]">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Limit</span>
            <select
              value={eventLimit}
              onChange={(e) => setEventLimit(Number(e.target.value))}
              className="f-input w-full"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </label>
          <button type="submit" className="f-button" disabled={eventsLoading}>
            {eventsLoading ? 'Loading…' : 'Load events'}
          </button>
        </form>

        {eventsError && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {eventsError}
          </div>
        )}

        {lookedUpUser && (
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-700">
            <span className="font-semibold text-slate-900">{pickUserLabel(lookedUpUser, lookedUpUser.id)}</span>
            <span className="text-slate-500">{lookedUpUser.email || lookedUpUser.id}</span>
            <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              {eventsTotal} event{eventsTotal !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {eventsLoading ? (
          <div className="mt-6 flex flex-col items-center justify-center gap-3 py-10">
            <FaSpinner className="fa-spin text-2xl text-primary" aria-hidden />
            <span className="text-sm text-slate-600">Loading events…</span>
          </div>
        ) : events.length > 0 ? (
          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className={thClass}>Time</th>
                  <th className={thClass}>Platform</th>
                  <th className={thClass}>Action</th>
                  <th className={thClass}>Visit / details</th>
                  <th className={thClass}>UA</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => {
                  const ts = ev.timestamp ? dayjs(ev.timestamp) : null;
                  const details = ev.details && typeof ev.details === 'object' ? ev.details : {};
                  const detailBits = [];
                  if (ev.page) detailBits.push(`page: ${ev.page}`);
                  if (ev.signId) detailBits.push(`sign: ${ev.signId}`);
                  if (details.query) detailBits.push(`query: ${details.query}`);
                  if (details.resultsCount != null) detailBits.push(`results: ${details.resultsCount}`);
                  return (
                    <tr key={`${ev.timestamp || ''}-${i}`} className="hover:bg-slate-50/80">
                      <td className={`${tdClass} whitespace-nowrap text-xs text-slate-500`}>
                        {ts?.isValid() ? ts.format('MMM D, h:mm A') : '—'}
                      </td>
                      <td className={tdClass}>
                        <PlatformBadge platform={ev.platform} />
                      </td>
                      <td className={tdClass}>
                        <code className="font-mono text-xs font-semibold text-primary">{ev.action || '—'}</code>
                      </td>
                      <td className={tdClass}>
                        <div className="font-medium text-slate-900">{formatActivityVisit(ev)}</div>
                        {detailBits.length > 0 && (
                          <div className="mt-1 text-xs text-slate-500">{detailBits.join(' · ')}</div>
                        )}
                      </td>
                      <td className={`${tdClass} max-w-[180px] truncate text-xs text-slate-500`} title={ev.userAgent || ''}>
                        {[ev.browser, ev.os].filter(Boolean).join(' / ') || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : lookedUpUser && !eventsError ? (
          <p className="mt-4 text-sm text-slate-500">No events found for this user.</p>
        ) : null}
      </div>
    </div>
  );
}

function BreakdownList({ title, rows, keyName }) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400">—</p>
      ) : (
        <ul className="space-y-1.5">
          {list.slice(0, 8).map((r) => (
            <li key={String(r[keyName])} className="flex items-center justify-between gap-3 text-sm">
              <span className="truncate text-slate-700">{r[keyName] || 'unknown'}</span>
              <span className="shrink-0 font-semibold text-slate-900">{Number(r.count || 0).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
